/**
 * Client for loading asset price data via the loadAssetPrices Firebase callable.
 * Supports both stocks (flat response) and crypto (nested response) from Tiingo.
 * Large date ranges are chunked into frequency-appropriate segments fetched in parallel.
 */

import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import { waitForAuth } from '../../../data/authUtils';
import type { OHLCVPoint, PriceRow, CryptoAssetPriceEntry } from './types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Tiingo caps responses at ~5000 rows. Chunk sizes are conservative estimates
// of how many days fit in one request at each frequency.
const CHUNK_DAYS_BY_FREQ: Record<ResampleFreq, number> = {
    '1min': 3, // 1440 pts/day → ~3.5 days
    '5min': 15, // 288 pts/day  → ~17 days
    '15min': 45, // 96 pts/day   → ~52 days
    '1hour': 180, // 24 pts/day   → ~208 days
    '4hour': 700, // 6 pts/day    → ~833 days
    '12hour': 1000, // 2 pts/day    → ~2500 days
    '1day': 1000, // 1 pt/day     → ~5000 days
};

type ResampleFreq =
    | '1min'
    | '5min'
    | '15min'
    | '1hour'
    | '4hour'
    | '12hour'
    | '1day';

interface FetchOptions {
    signal?: AbortSignal;
}

interface LoadOptions extends FetchOptions {
    frequency?: ResampleFreq;
    aggregate?: 'daily' | 'none';
    // UI-level candle frequency; coarser than daily is handled client-side
    // after the daily aggregation step.
    candleFrequency?: '1H' | '1D' | '1W' | '1M';
}

// -- Normalization --

/**
 * Detect stock vs crypto response shape and return a flat array of OHLCV points.
 * Stocks: { data: [{date, open, high, low, close}, ...] }
 * Crypto: { data: [{ticker, baseCurrency, quoteCurrency, priceData: [...]}] }
 */
function normalizeResponse(raw: unknown): OHLCVPoint[] {
    if (!raw || typeof raw !== 'object') {
        console.error('normalizeResponse: unexpected raw shape', raw);
        return [];
    }
    const data = (raw as Record<string, unknown>).data;
    if (data === undefined) {
        console.error('normalizeResponse: response missing "data" field', raw);
        return [];
    }
    if (!Array.isArray(data)) {
        console.error(
            'normalizeResponse: "data" field is not an array',
            typeof data
        );
        return [];
    }
    if (data.length === 0) return [];

    // Crypto shape: first element has `priceData` array
    if (data[0]?.priceData) {
        const entries = data as CryptoAssetPriceEntry[];
        return entries.flatMap(entry => entry.priceData.map(rowToPoint));
    }

    // Stock shape: flat array of rows
    return (data as PriceRow[]).map(rowToPoint);
}

function rowToPoint(row: PriceRow): OHLCVPoint {
    return {
        t: new Date(row.date),
        open: row.open,
        high: row.high,
        low: row.low,
        close: row.close,
        vol: 0,
    };
}

// -- Daily aggregation --

/**
 * Collapse sub-daily points into daily OHLCV by calendar day.
 * The `t` field is set to midnight UTC of each calendar day so the chart
 * X-axis shows clean dates (stocks report at 17:00 UTC, crypto at 00:00/12:00).
 */
function aggregateToDaily(points: OHLCVPoint[]): OHLCVPoint[] {
    if (points.length === 0) return [];

    const byDay = new Map<string, OHLCVPoint[]>();
    for (const pt of points) {
        const key = pt.t.toISOString().slice(0, 10); // "YYYY-MM-DD"
        const bucket = byDay.get(key);
        if (bucket) {
            bucket.push(pt);
        } else {
            byDay.set(key, [pt]);
        }
    }

    const result: OHLCVPoint[] = [];
    for (const [dayStr, bucket] of byDay) {
        bucket.sort((a, b) => a.t.getTime() - b.t.getTime());
        result.push({
            t: new Date(dayStr + 'T00:00:00Z'),
            open: bucket[0].open,
            high: Math.max(...bucket.map(p => p.high)),
            low: Math.min(...bucket.map(p => p.low)),
            close: bucket[bucket.length - 1].close,
            vol: bucket.reduce((sum, p) => sum + p.vol, 0),
        });
    }

    return result.sort((a, b) => a.t.getTime() - b.t.getTime());
}
// -- Weekly / monthly aggregation --

// ISO week: collapse points to the Monday of their ISO week.
function aggregateToWeekly(points: OHLCVPoint[]): OHLCVPoint[] {
    if (points.length === 0) return [];

    const weekStart = (d: Date): Date => {
        const copy = new Date(d);
        const day = copy.getUTCDay();
        // shift so Monday = 0
        const diff = day === 0 ? -6 : 1 - day;
        copy.setUTCDate(copy.getUTCDate() + diff);
        copy.setUTCHours(0, 0, 0, 0);
        return copy;
    };

    const byWeek = new Map<number, OHLCVPoint[]>();
    for (const pt of points) {
        const key = weekStart(pt.t).getTime();
        const bucket = byWeek.get(key);
        if (bucket) bucket.push(pt);
        else byWeek.set(key, [pt]);
    }

    const result: OHLCVPoint[] = [];
    for (const [keyMs, bucket] of byWeek) {
        bucket.sort((a, b) => a.t.getTime() - b.t.getTime());
        result.push({
            t: new Date(keyMs),
            open: bucket[0].open,
            high: Math.max(...bucket.map(p => p.high)),
            low: Math.min(...bucket.map(p => p.low)),
            close: bucket[bucket.length - 1].close,
            vol: bucket.reduce((sum, p) => sum + p.vol, 0),
        });
    }

    return result.sort((a, b) => a.t.getTime() - b.t.getTime());
}

// Collapse points to the first of their calendar month (UTC).
function aggregateToMonthly(points: OHLCVPoint[]): OHLCVPoint[] {
    if (points.length === 0) return [];

    const byMonth = new Map<string, OHLCVPoint[]>();
    for (const pt of points) {
        const key = pt.t.toISOString().slice(0, 7); // "YYYY-MM"
        const bucket = byMonth.get(key);
        if (bucket) bucket.push(pt);
        else byMonth.set(key, [pt]);
    }

    const result: OHLCVPoint[] = [];
    for (const [monthStr, bucket] of byMonth) {
        bucket.sort((a, b) => a.t.getTime() - b.t.getTime());
        result.push({
            t: new Date(monthStr + '-01T00:00:00Z'),
            open: bucket[0].open,
            high: Math.max(...bucket.map(p => p.high)),
            low: Math.min(...bucket.map(p => p.low)),
            close: bucket[bucket.length - 1].close,
            vol: bucket.reduce((sum, p) => sum + p.vol, 0),
        });
    }

    return result.sort((a, b) => a.t.getTime() - b.t.getTime());
}

// -- Chunked fetching --

interface Chunk {
    startDate: string;
    endDate: string;
}

const MAX_CHUNKS = 30;

function buildChunks(
    startDate: Date,
    endDate: Date,
    frequency: ResampleFreq
): Chunk[] {
    const chunkDays = CHUNK_DAYS_BY_FREQ[frequency];
    const chunks: Chunk[] = [];
    let cursor = endDate.getTime();
    const startMs = startDate.getTime();

    while (cursor > startMs && chunks.length < MAX_CHUNKS) {
        const chunkStart = Math.max(cursor - chunkDays * MS_PER_DAY, startMs);
        chunks.push({
            startDate: new Date(chunkStart).toISOString(),
            endDate: new Date(cursor).toISOString(),
        });
        cursor = chunkStart;
    }

    return chunks;
}

async function fetchChunk(
    ticker: string,
    assetType: string,
    startDate: string,
    endDate: string,
    frequency: ResampleFreq,
    signal?: AbortSignal
): Promise<OHLCVPoint[]> {
    const functions = firebase.app().functions('europe-west6');
    const callable = functions.httpsCallable('loadAssetPrices');

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const result = await callable({
        ticker,
        assetType,
        startDate,
        endDate,
        resampleFreq: frequency,
    });

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    return normalizeResponse(result.data);
}

// -- Deduplication --

function dedupeByDate(points: OHLCVPoint[]): OHLCVPoint[] {
    const seen = new Set<number>();
    return points.filter(pt => {
        const key = pt.t.getTime();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

// -- Concurrency-limited fetching --

const MAX_CONCURRENT = 6;

async function fetchAllChunks(
    chunks: Chunk[],
    fn: (chunk: Chunk) => Promise<OHLCVPoint[]>
): Promise<OHLCVPoint[][]> {
    const results: OHLCVPoint[][] = new Array(chunks.length);
    let next = 0;

    async function worker(): Promise<void> {
        while (next < chunks.length) {
            const idx = next++;
            results[idx] = await fn(chunks[idx]);
        }
    }

    await Promise.all(
        Array.from({ length: Math.min(MAX_CONCURRENT, chunks.length) }, () =>
            worker()
        )
    );
    return results;
}

// -- Main entry --

export async function loadAssetPrices(
    ticker: string,
    assetType: string,
    startDate: Date,
    endDate: Date,
    options?: LoadOptions
): Promise<OHLCVPoint[]> {
    await waitForAuth();

    const signal = options?.signal;
    const candleFrequency = options?.candleFrequency ?? '1D';
    // 1H fetches at hourly resolution and skips daily aggregation
    const frequency =
        candleFrequency === '1H' ? '1hour' : options?.frequency ?? '12hour';
    const aggregate =
        candleFrequency === '1H' ? 'none' : options?.aggregate ?? 'daily';
    const chunks = buildChunks(startDate, endDate, frequency);

    const results = await fetchAllChunks(chunks, chunk =>
        fetchChunk(
            ticker,
            assetType,
            chunk.startDate,
            chunk.endDate,
            frequency,
            signal
        )
    );

    let points = results.flat();

    if (aggregate === 'daily') {
        points = aggregateToDaily(points);
    }

    if (candleFrequency === '1W') {
        points = aggregateToWeekly(points);
    } else if (candleFrequency === '1M') {
        points = aggregateToMonthly(points);
    }

    points.sort((a, b) => a.t.getTime() - b.t.getTime());
    return dedupeByDate(points);
}
